// Project defaults, (de)serialisation and schema migration (F-13.3c, docs/04-data-model.md).

import type { LayoutState, Project, UserSettings, ViewAdjustment } from './types';
import { ZOOM_LEVELS } from './types';
import { LEVELS_IDENTITY } from './color';

export const SCHEMA_VERSION = '1.0';
export const PROJECT_EXT = '.acproj';

export function defaultLayout(): LayoutState {
  return {
    layout: 'side-by-side-zoom',
    selectedB: 'B1',
    compareMode: 'side-by-side',
    sideBySideDirection: 'horizontal',
    wipe: { position: 0.5, angleDeg: 0 },
    overlayOpacity: 0.5,
    difference: { gain: 4, display: 'gray', lumaOnly: false, maskOnly: false },
    view: { zoomPercent: 'fit', center: { x: 0.5, y: 0.5 }, sampling: 'nearest' },
    crosshair: { position: { x: 960, y: 540 }, zoomPercent: 400, visible: true },
    zoomPaneRatio: 0.5,
    zoomDifference: false,
    zoomGrid: false,
    channel: 'rgb',
    textureView: false,
    maskOverlay: false,
    maskFocus: false,
  };
}

export function defaultViewAdjustment(): ViewAdjustment {
  return {
    enabled: false,
    linked: true,
    exposureStops: 0,
    gamma: 1,
    levels: { ...LEVELS_IDENTITY },
  };
}

export function createProject(name: string, appVersion: string, now = new Date()): Project {
  const iso = now.toISOString();
  return {
    schemaVersion: SCHEMA_VERSION,
    appVersion,
    name,
    createdAt: iso,
    updatedAt: iso,
    editTypes: [],
    sources: [],
    compareScale: 1,
    playback: { tick: 0, loopMode: 'loop', speed: 1 },
    layout: defaultLayout(),
    viewAdjustment: defaultViewAdjustment(),
    audioSourceId: 'A',
    audioVolume: 1,
    timecodeMode: 'embedded',
    originalFramesOnly: false,
    tickScale: 1,
    maskSources: [],
    regions: [],
    ratings: [],
    qualityChecks: {},
    aiBrief: '',
  };
}

export function defaultSettings(browserLang?: string): UserSettings {
  const lang = (browserLang ?? '').toLowerCase().startsWith('zh') ? 'zh-TW' : 'en';
  return {
    language: lang,
    degradeMode: 'smooth',
    frameNumberBase: 0,
    flickerHz: 1,
    zoomCenterMark: false,
    alignAutoDifference: true,
  };
}

const KNOWN_KEYS = new Set<string>(Object.keys(createProject('', '')));

export function serializeProject(p: Project, appVersion: string, now = new Date()): string {
  const { extra, ...rest } = p;
  const out: Record<string, unknown> = { ...(extra ?? {}), ...rest, appVersion, updatedAt: now.toISOString() };
  return JSON.stringify(out, null, 2);
}

export type ParseResult =
  | { ok: true; project: Project; readOnly: boolean; warnings: string[] }
  | { ok: false; errorKey: string };

function compareVersion(a: string, b: string): number {
  const pa = a.split('.').map((x) => parseInt(x, 10) || 0);
  const pb = b.split('.').map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/** Migrations keyed by the version they upgrade from. Each returns the next version's object. */
const MIGRATIONS: Record<string, (o: Record<string, unknown>) => Record<string, unknown>> = {
  // '0.9': (o) => ({ ...o, schemaVersion: '1.0' }),
};

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function parseProject(text: string): ParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, errorKey: 'error.project.parse' };
  }
  if (!isObject(raw) || typeof raw.schemaVersion !== 'string' || !Array.isArray(raw.sources))
    return { ok: false, errorKey: 'error.project.invalid' };
  let obj: Record<string, unknown> = raw;
  const warnings: string[] = [];
  let readOnly = false;
  if (compareVersion(obj.schemaVersion as string, SCHEMA_VERSION) > 0) {
    readOnly = true;
    warnings.push('project.warn.newer');
  } else {
    let guard = 0;
    while (obj.schemaVersion !== SCHEMA_VERSION && guard++ < 20) {
      const m = MIGRATIONS[obj.schemaVersion as string];
      if (!m) return { ok: false, errorKey: 'error.project.version' };
      obj = m(obj);
    }
  }
  // Fill missing fields with defaults so older files keep working; keep unknown fields in `extra`.
  const base = createProject(String(obj.name ?? ''), String(obj.appVersion ?? ''));
  const extra: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) if (!KNOWN_KEYS.has(k)) extra[k] = v;
  const layout = { ...base.layout, ...(isObject(obj.layout) ? (obj.layout as Partial<LayoutState>) : {}) };
  // The quad zoom layout was removed (2026-09-30): old files open in the quad layout.
  if ((layout.layout as string) === 'quad-zoom') layout.layout = 'quad';
  // Zoom levels are 100–3000 %: snap to the nearest level.
  if (isObject(layout.crosshair)) {
    const z = Number(layout.crosshair.zoomPercent) || 400;
    const near = ZOOM_LEVELS.reduce((a, b) => (Math.abs(b - z) < Math.abs(a - z) ? b : a));
    layout.crosshair = { ...layout.crosshair, zoomPercent: near };
  }
  const project: Project = {
    ...base,
    ...(obj as Partial<Project>),
    layout,
    viewAdjustment: { ...base.viewAdjustment, ...(isObject(obj.viewAdjustment) ? (obj.viewAdjustment as Partial<ViewAdjustment>) : {}) },
    playback: { ...base.playback, ...(isObject(obj.playback) ? (obj.playback as Partial<Project['playback']>) : {}) },
    schemaVersion: readOnly ? (obj.schemaVersion as string) : SCHEMA_VERSION,
  };
  if (Object.keys(extra).length) project.extra = extra;
  else delete project.extra;
  return { ok: true, project, readOnly, warnings };
}

/** Fingerprint from file size plus the first and last 2 MB (docs/04-data-model.md). */
export async function fingerprintFile(file: Blob): Promise<string> {
  const chunk = 2 * 1024 * 1024;
  const head = await file.slice(0, Math.min(chunk, file.size)).arrayBuffer();
  const tail = await file.slice(Math.max(0, file.size - chunk)).arrayBuffer();
  const sizeBytes = new TextEncoder().encode(String(file.size));
  const all = new Uint8Array(head.byteLength + tail.byteLength + sizeBytes.byteLength);
  all.set(new Uint8Array(head), 0);
  all.set(new Uint8Array(tail), head.byteLength);
  all.set(sizeBytes, head.byteLength + tail.byteLength);
  const digest = await crypto.subtle.digest('SHA-256', all);
  return Array.from(new Uint8Array(digest).slice(0, 16), (b) => b.toString(16).padStart(2, '0')).join('');
}

export function newId(prefix = 'id'): string {
  const rnd = crypto.getRandomValues(new Uint32Array(2));
  return `${prefix}_${rnd[0].toString(36)}${rnd[1].toString(36)}`;
}
